/**
 * TransferPanel Component
 *
 * Inline panel for Transfer/Conference operations.
 * Replaces the modal-based TransferDialog for better iframe compatibility.
 */

import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTransferData } from '../../hooks/useTransferData';
import { useTransferActions } from '../../hooks/useTransferActions';
import { TransferQueueTab } from './TransferQueueTab';
import { TransferAgentTab } from './TransferAgentTab';
import { TransferNumberTab } from './TransferNumberTab';
import { TransferPanelTabs, TransferTabType } from './TransferPanelTabs';
import styles from './TransferPanel.module.css';

interface TransferPanelProps {
  mode: 'transfer' | 'conference';
  contactId: string;
  onClose: () => void;
  onComplete: (target?: string) => void;
}

export function TransferPanel({ mode, contactId, onClose, onComplete }: TransferPanelProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TransferTabType>('queue');
  const isConferenceMode = mode === 'conference';

  const {
    skills,
    agents,
    searchQuery,
    setSearchQuery,
    filteredSkills,
    filteredAgents,
    isLoadingData,
    loadError,
    clearSearch,
  } = useTransferData();

  const { isLoading, error, handleSelectSkill, handleSelectAgent, handleDialPhone, clearError } =
    useTransferActions({
      contactId,
      isConferenceMode,
      onComplete,
    });

  const handleTabChange = useCallback(
    (tab: TransferTabType) => {
      setActiveTab(tab);
      clearSearch();
      clearError();
    },
    [clearSearch, clearError]
  );

  const displayError = error || loadError;
  const title = isConferenceMode
    ? t('call:transfer.addToConference')
    : t('call:transfer.transferCall');

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <h3 className={styles.title}>{title}</h3>
        <button
          className={styles.closeBtn}
          onClick={onClose}
          disabled={isLoading}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        </button>
      </div>

      <TransferPanelTabs
        activeTab={activeTab}
        isLoading={isLoading}
        onTabChange={handleTabChange}
      />

      <div className={styles.content}>
        {isLoadingData ? (
          <div className={styles.loadingState}>
            <div className={styles.spinner} />
            <span>{t('call:transfer.loadingOptions')}</span>
          </div>
        ) : isLoading ? (
          <div className={styles.loadingState}>
            <div className={styles.spinner} />
            <span>
              {isConferenceMode ? t('call:transfer.addingParty') : t('call:transfer.transferring')}
            </span>
          </div>
        ) : (
          <>
            {displayError && <div className={styles.error}>{displayError}</div>}

            {activeTab === 'queue' && (
              <TransferQueueTab
                skills={skills}
                filteredSkills={filteredSkills}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                onSelectSkill={handleSelectSkill}
              />
            )}

            {activeTab === 'agent' && (
              <TransferAgentTab
                agents={agents}
                filteredAgents={filteredAgents}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                onSelectAgent={handleSelectAgent}
              />
            )}

            {activeTab === 'number' && (
              <TransferNumberTab
                isConferenceMode={isConferenceMode}
                onDialPhone={handleDialPhone}
              />
            )}
          </>
        )}
      </div>

      <div className={styles.footer}>
        <button className={styles.cancelBtn} onClick={onClose} disabled={isLoading}>
          {t('common:buttons.cancel')}
        </button>
      </div>
    </div>
  );
}

export default TransferPanel;
